import {
  createContext,
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { isNil } from "@izettle/react"

import {
  DropdownChildren,
  DropdownPropsWithChildren,
  DropdownValue,
  DropdownTexts,
  OptionProps,
  isMultipleDropdown,
} from "../types"

import {
  castValueToArray,
  filterSelectedOptions,
  getIsOptionSelectedByProps,
  getPropsFromChildrenOptions,
  toggleArrayItem,
} from "./utils"

type ContextValue = {
  children: DropdownChildren
  className?: string
  close(): void
  displayText: string
  done(): void
  dropdownProps: DropdownPropsWithChildren
  getIsOptionSelected(props: OptionProps): boolean
  handleValueChange(value: DropdownValue): void
  isMobileView: boolean
  isMultiple: boolean
  isOpen: boolean
  open(): void
  placeholder?: string
  reset(): void
  texts: DropdownTexts
  toggle(): void
  value: DropdownValue[]
}

type ProviderProps = {
  dropdownProps: DropdownPropsWithChildren
}

export const DropdownContext = createContext<ContextValue>({
  children: [],
  className: "",
  close: () => undefined,
  displayText: "",
  done: () => undefined,
  dropdownProps: {} as any,
  getIsOptionSelected: () => false,
  handleValueChange: () => undefined,
  isMobileView: false,
  isMultiple: false,
  isOpen: false,
  open: () => undefined,
  placeholder: undefined,
  reset: () => undefined,
  texts: {} as DropdownTexts,
  toggle: () => undefined,
  value: [],
})

// Context in dropdown component does following things:
// 1. Option components be able to reach out properties like value and onChange;
// 2. Encapsulate logic, that's responsible for accessing and changing component's state:
//    every children component can get access to isOpen state of dropdown, and change it;
// 3. Encapsulate logic, that's responsible for accessing and changing component's value:
//    no matter what value isMultiple is - context's interface will remain, i.e. children components
//    might not care whether dropdown is multiple or not.
export const Provider: FC<ProviderProps> = memo(
  ({ children: providerChildren, dropdownProps }) => {
    const {
      children: optionElements,
      className,
      isMobileView = false,
      isMultiple = false,
      overrideDisplayText,
      placeholder,
      onChange,
      onChangeSelected,
      value,
    } = dropdownProps

    // stateValue is component's value, saved in state
    // stateValueAsArray is just a helper that makes life easier when working with component's value
    const [stateValue, setStateValue] = useState(value)
    const stateValueAsArray = useMemo(
      () => castValueToArray(stateValue, isMultiple),
      [isMultiple, stateValue],
    )

    const [isOpen, setIsOpen] = useState(false)
    const previousValue = useRef(value)

    useEffect(() => setStateValue(value), [value])

    const open = useCallback(() => {
      // Save the value at the moment dropdown is opened.
      // This value is component's previous value.
      // While component is opened - it's value can be changed via UI,
      // and then reset to previous value
      previousValue.current = value

      setIsOpen(true)
    }, [value])

    const close = useCallback(() => setIsOpen(false), [])
    const toggle = useCallback(() => (isOpen ? close() : open()), [close, isOpen, open])

    const reset = useCallback(() => {
      setStateValue(previousValue.current)
      onChangeSelected?.(previousValue.current)
    }, [onChangeSelected])

    // In mobile multi-select changes of value are bulked and submitted with button press.
    // This callback can be used to react on effective change of the value
    // (make server call for example),
    // instead of onChangeSelected, which will be called on every change of value

    // In desktop and mobile single-select versions this callback
    // is executed right after onChangeSelected
    const done = useCallback(
      (newValue: DropdownValue | DropdownValue[] = stateValue) => onChange?.(newValue),
      [onChange, stateValue],
    )

    const handleValueChange = useCallback(
      (optionValue: DropdownValue) => {
        const newValue: DropdownValue | DropdownValue[] = !isMultiple
          ? optionValue
          : toggleArrayItem(castValueToArray(stateValue, true), optionValue)

        setStateValue(newValue)
        onChangeSelected?.(newValue)

        if (!isMobileView || !isMultiple) {
          done(newValue)
        }
      },
      [done, isMobileView, isMultiple, onChangeSelected, stateValue],
    )

    // Dropdown options to render are coming in children property.
    // But an array of structured option data is still needed.
    // Extract text and value of each option and map it to an object:
    const options = useMemo(
      () => getPropsFromChildrenOptions(optionElements),
      [optionElements],
    )
    const selected = useMemo(
      () => filterSelectedOptions(stateValueAsArray, options),
      [options, stateValueAsArray],
    )

    const displayText = isNil(overrideDisplayText)
      ? selected
          .map(({ children, label }) => {
            const values = children ?? label
            return Array.isArray(values) ? values.join("") : values
          })
          .join(", ")
      : overrideDisplayText

    const getIsOptionSelected = useCallback(
      (option: OptionProps) => getIsOptionSelectedByProps(selected, option),
      [selected],
    )

    const contextValueProps = {
      className,
      close,
      displayText,
      done,
      dropdownProps,
      getIsOptionSelected,
      handleValueChange,
      isMobileView,
      isMultiple,
      isOpen,
      open,
      placeholder,
      reset,
      toggle,
      children: optionElements,
      value: stateValueAsArray,
      texts: {} as DropdownTexts,
    }

    const contextValue: ContextValue = useMemo(() => {
      return isMultipleDropdown(contextValueProps.dropdownProps)
        ? { ...contextValueProps, texts: contextValueProps.dropdownProps.texts }
        : contextValueProps
    }, Object.values(contextValueProps))

    return (
      <DropdownContext.Provider value={contextValue}>
        {providerChildren}
      </DropdownContext.Provider>
    )
  },
)
